import {
  ChangeDetectionStrategy,
  Component,
  computed,
  DestroyRef,
  effect,
  ElementRef,
  HostListener,
  inject,
  input,
  output,
  viewChild,
} from '@angular/core';
import { DOCUMENT } from '@angular/common';
import { TranslateModule } from '@ngx-translate/core';
import { BreakpointService } from '../../../services/breakpoint.service';
import { Button } from '../../atoms/button/button';

export type ConfirmModalVariant = 'danger' | 'warning' | 'info';

@Component({
  selector: 'om-confirm-modal',
  imports: [TranslateModule, Button],
  templateUrl: './confirm-modal.html',
  styleUrl: './confirm-modal.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ConfirmModal {
  readonly isOpen = input(false);
  readonly icon = input('warning');
  readonly variant = input<ConfirmModalVariant>('danger');
  readonly title = input('');
  readonly message = input('');
  readonly confirmLabel = input('');
  readonly cancelLabel = input('');

  readonly confirmed = output<void>();
  readonly cancelled = output<void>();

  private readonly bp = inject(BreakpointService);
  private readonly doc = inject(DOCUMENT);
  readonly isMobile = computed(() => this.bp.isMobile());
  readonly portalRef = viewChild<ElementRef<HTMLElement>>('modalPortal');

  readonly iconBgClass = computed(() => {
    switch (this.variant()) {
      case 'danger': return 'bg-primary/10 dark:bg-primary/15 text-primary';
      case 'warning': return 'bg-amber-50 dark:bg-amber-900/20 text-amber-600 dark:text-amber-400';
      case 'info': return 'bg-primary/10 dark:bg-primary/15 text-primary';
    }
  });

  constructor() {
    effect(() => {
      const wrapper = this.portalRef()?.nativeElement;
      if (!wrapper) return;

      if (this.isOpen()) {
        this.doc.body.appendChild(wrapper);
      } else if (wrapper.parentElement === this.doc.body) {
        wrapper.remove();
      }
    });

    inject(DestroyRef).onDestroy(() => {
      const wrapper = this.portalRef()?.nativeElement;
      if (wrapper?.parentElement === this.doc.body) {
        wrapper.remove();
      }
    });
  }

  onConfirm(): void {
    this.confirmed.emit();
  }

  onCancel(): void {
    this.cancelled.emit();
  }

  @HostListener('document:keydown.escape')
  onEscape(): void {
    if (this.isOpen()) this.onCancel();
  }
}
